import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Button, Typography } from '@mui/material';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import MDialog from '../../../components/controls/MDialog';
import FilterBar from './FilterBar';
import CityStateGrid from './CityStateGrid';
import CheckAbbreviationDialog from './CheckAbbreviationDialog';
import MergeDialogs from './MergeDialogs';
import type {
    AbbrCheckResult, CityAbbreviationMatch, CityAbbreviationSelfMatch, CityStateDetailPopupProps,
    CityStateRow, PendingMerge, SelfMatchChoice,
} from './types';
import { getCityAbbreviation, getRowKey, isRowInvalid, mergeCityAbbreviation } from './utils';

/**
 * Modal dialog that lists orphaned City/State groups (`smallspider_event` rows with
 * `VenueId = 0`) for the selected market type, backed by `CityStateCheck/Filter`.
 *
 * Supports inline editing of `Description`, `Status`, and `CityAbbreviation` (`Description` is
 * disabled for a genuinely existing record — `isExist = true` and not redirected via
 * `_forceUpdateExisting` — since only its `CityAbbreviation` is ever persisted, see
 * {@link handleConfirmMerge}). `StateName` and `Country` are normally read-only, sourced from the
 * `venue_city_states` match, but become editable for the whole session on a row that was
 * **originally invalid** (per `_originallyInvalid`, a snapshot of `isRowInvalid` taken when the
 * row was loaded), so the operator can supply the missing value; editability is decided solely by
 * that original snapshot rather than the row's current (possibly now-valid) state or the field's
 * current emptiness, so the cell never reverts to read-only once filled in. An `Exists` radio filter (**All / Yes / No**, defaulting to **No** — the `No` selection
 * does *not* auto-check invalid rows), an **Invalid Only** filter, plus City and State text
 * filters to narrow the grid. Every row — including `Exists = Yes` — has a functional selection
 * checkbox; merging an `Exists = Yes` row (that hasn't been redirected via **Check City
 * Abbreviation**) only submits its `CityAbbreviation` change, leaving its other fields untouched
 * (see {@link handleConfirmMerge}). A **Check City Abbreviation** button sends the checked rows to
 * `CityStateCheck/CheckCityAbbreviation`, which fuzzy-matches them (ignored-words-normalized
 * City + exact State) against `venue_city_states` *and* against each other. Clicking the button
 * only fetches and displays the analysis results in {@link CheckAbbreviationDialog} — it never
 * checks/unchecks/updates/removes any row on its own. Each of the dialog's two steps
 * (**Apply Selected Changes**, **Update Existing City Abbreviation**) *stages* its changes into
 * {@link pendingAbbrRows} and keeps the dialog open (see {@link handleApplySelectedChanges},
 * {@link handleUpdateExisting}); nothing is written back to `rows`/the grid until the operator
 * clicks the dialog's **OK** ({@link handleCommitAbbrCheck}), or discarded entirely via
 * **Cancel** ({@link handleCancelAbbrCheck}).
 *
 * A **Preserve Selection** checkbox (internally `editingMode`, default off/"Normal") changes how
 * row selection behaves across filter changes:
 * - **Off (Normal)** — the existing behavior: switching to the `No` filter auto-selects every
 *   visible valid row, and hidden (filtered-out) rows' selection is not tracked.
 * - **On** — the full selection state (which rows are checked) is preserved across filter
 *   changes via {@link selectionMemoryRef}, keyed by each row's stable `getRowKey`. Filtering
 *   narrows the *visible* rows only; API actions (Check City Abbreviation, Insert/Merge) still
 *   operate only on the currently visible/selected rows, but once filters are widened or cleared
 *   again, previously checked rows (and any edits made to them, which persist naturally since
 *   the underlying row objects are mutated in place by the grid) are restored exactly as left.
 *
 * An **Insert/Merge** button — shown only once one or more rows are selected — first shows a
 * confirmation dialog with the Insert/Update breakdown, then submits the selected rows to
 * `CityStateCheck/BulkMerge` (each row is inserted or updated in `venue_city_states` based on its
 * `isExist`/redirected flag).
 *
 * Rows whose original (server) `CityAbbreviation` is non-empty are rendered in bold, and
 * the `Exists` column is color-coded (red = missing / green = already exists / blue = missing
 * but redirected to update) to make the groups easy to tell apart at a glance. A row is
 * **invalid** — and highlighted in red — when its `stateId` isn't exactly 2 uppercase letters,
 * its `stateName`/`country` is missing, or its `city` is 2 characters or shorter (see
 * {@link isRowInvalid}); Insert/Merge is blocked with a warning stating the precise reason
 * whenever an invalid row is selected (this check always uses the row's *current* data). An
 * originally-invalid row's `stateName`/`country` cells become editable in the grid so the
 * operator can fix them in place, and stay editable for the whole session — even once filled in
 * with a value that makes the row valid (see `_originallyInvalid`).
 *
 * A bulk **Apply Status** control (0/1/2, default 1) applies the chosen status to every
 * currently loaded row regardless of the active filters.
 *
 * @param props - {@link CityStateDetailPopupProps}
 */
const CityStateDetailPopup: React.FC<CityStateDetailPopupProps> = ({ open, onClose, marketType, onMerged }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const agGridRef = useRef<any>(null);
    const [rows, setRows] = useState<CityStateRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [pendingMerge, setPendingMerge] = useState<PendingMerge | null>(null);
    /** Tri-state `Exists` filter driven by the All/Yes/No radio group: `false` (No, default), `true` (Yes), or `null` (All). */
    const [existsFilter, setExistsFilter] = useState<boolean | null>(false);
    const [cityFilter, setCityFilter] = useState<string>('');
    const [stateFilter, setStateFilter] = useState<string>('');
    const [invalidOnlyFilter, setInvalidOnlyFilter] = useState<boolean>(false);
    /** `false` (default, Normal) or `true` (Editing) — see the component doc comment above. */
    const [editingMode, setEditingMode] = useState<boolean>(false);
    /** Number of rows currently checked in the grid; drives the Insert/Merge and Check City Abbreviation buttons' visibility. */
    const [selectedCount, setSelectedCount] = useState<number>(0);
    const [checkingAbbr, setCheckingAbbr] = useState<boolean>(false);
    const [abbrCheckResult, setAbbrCheckResult] = useState<AbbrCheckResult | null>(null);
    /** Per-row (keyed by `CityStateRow.id`) selected match; every group defaults to no entry ("None") until the operator explicitly picks one via radio. */
    const [matchSelections, setMatchSelections] = useState<Record<string, CityAbbreviationMatch>>({});
    /** Status value picked in the bulk Apply Status control (0, 1, or 2; default 2). */
    const [applyStatusValue, setApplyStatusValue] = useState<string>('2');
    /** Per-pair (keyed by `${keepItemId}|${dropItemId}`) Keep/Uncheck/None choice for a self-match; `'none'` (default) leaves the pair unchanged. Not applied until Apply Selected Changes is clicked. */
    const [selfMatchChoice, setSelfMatchChoice] = useState<Record<string, SelfMatchChoice>>({});
    /** Per-pair (keyed by `${keepItemId}|${dropItemId}`) editable merged CityAbbreviation, prompted after a self-match is detected. */
    const [selfMatchAbbreviations, setSelfMatchAbbreviations] = useState<Record<string, string>>({});
    /** Whether the operator is currently applying a City Abbreviation change. */
    const [isApplyingAbbr, setIsApplyingAbbr] = useState<boolean>(false);
    /**
     * Staged (not-yet-committed) row snapshot for the current Check Abbreviation dialog session.
     * `null` means neither step has been applied yet. Step 1/2 each derive their computation from
     * `pendingAbbrRows ?? rows` so the two steps compose, but `rows` itself (and the grid) is only
     * updated once the operator clicks **OK** in {@link handleCommitAbbrCheck} — clicking **Cancel**
     * ({@link handleCancelAbbrCheck}) simply drops this snapshot, leaving `rows` untouched.
     */
    const [pendingAbbrRows, setPendingAbbrRows] = useState<CityStateRow[] | null>(null);
    /** Row ids to uncheck in the grid once {@link pendingAbbrRows} is committed (populated by Step 1's "drop" side). */
    const [pendingDeselectIds, setPendingDeselectIds] = useState<Set<string>>(new Set());
    /** Whether Step 1 (Apply Selected Changes) has already been staged in the current dialog session. */
    const [step1Completed, setStep1Completed] = useState<boolean>(false);
    /** Whether Step 2 (Update Existing City Abbreviation) has already been staged in the current dialog session. */
    const [step2Completed, setStep2Completed] = useState<boolean>(false);
    /**
     * Editing-mode-only: persists each row's checked state (keyed by {@link getRowKey}) across
     * filter changes, since AgGrid drops selection for row nodes it destroys when they're
     * filtered out. Not a React state value — it's only ever read/written from effects/handlers
     * that already run in response to a filter or selection change, so a ref avoids redundant
     * re-renders.
     */
    const selectionMemoryRef = useRef<Record<string, boolean>>({});

    /** `abbrCheckResult.matches` grouped by the originating row's `id`, feeding the grouped dialog table. */

    useEffect(() => {
        // Reset per-row match selections and the Check Abbreviation dialog's staged session state
        // whenever a new Check City Abbreviation result comes in (or the dialog is closed). Every
        // group — including single-match ones — defaults to no selection ("None"); the operator
        // must explicitly pick a match before Update Existing City Abbreviation acts on it.
        setMatchSelections({});
        setPendingAbbrRows(null);
        setPendingDeselectIds(new Set());
        setStep1Completed(false);
        setStep2Completed(false);
    }, [abbrCheckResult]);

    useEffect(() => {
        // Seed the default None choice (leave the pair unchanged) and the merged CityAbbreviation
        // preview for each self-match pair. Nothing is applied to the grid or the rows here —
        // that only happens once the operator clicks Apply Selected Changes.
        if (!abbrCheckResult?.selfMatches?.length) {
            setSelfMatchChoice({});
            setSelfMatchAbbreviations({});
            return;
        }
        const initialChoice: Record<string, SelfMatchChoice> = {};
        const initialAbbr: Record<string, string> = {};
        abbrCheckResult.selfMatches.forEach((sm) => {
            const pairKey = `${sm.keepItemId}|${sm.dropItemId}`;
            initialChoice[pairKey] = 'none';
            const keepRow = abbrCheckResult.selectedRows.find((r) => r.id === sm.keepItemId);
            const dropRow = abbrCheckResult.selectedRows.find((r) => r.id === sm.dropItemId);
            initialAbbr[pairKey] = mergeCityAbbreviation(keepRow?.cityAbbreviation, dropRow?.city || sm.dropCity, sm.dropCityAbbreviation);
        });
        setSelfMatchChoice(initialChoice);
        setSelfMatchAbbreviations(initialAbbr);
    }, [abbrCheckResult]);

    useEffect(() => {
        if (!open) return;
        // Reset filters, mode, and transient state to their defaults each time the popup is
        // (re)opened for a market type.
        setExistsFilter(false);
        setCityFilter('');
        setStateFilter('');
        setInvalidOnlyFilter(false);
        setEditingMode(false);
        setSelectedCount(0);
        setIsApplyingAbbr(false);
        selectionMemoryRef.current = {};
        setLoading(true);
        setRows([]);
        dataProvider
            .getList('CityStateCheck', {
                filter: { MarketType_eq: marketType || '' },
                pagination: { page: 1, perPage: 5000 },
                sort: { field: 'city', order: 'ASC' },
            })
            .then((response: { data?: CityStateRow[] }) => {
                const data = (response?.data || [])
                    .map((row) => {
                        const mapped: CityStateRow = { ...row, id: row.itemId, _originalCityAbbreviation: row.cityAbbreviation || '' };
                        // Snapshot validity at load time so editability (see CityStateGrid) tracks the
                        // row's original state even after the operator fixes it in place.
                        return { ...mapped, _originallyInvalid: isRowInvalid(mapped) };
                    })
                    // Missing groups (isExist = No) surface first so operators triage them before reviewing existing ones.
                    .sort((a, b) => Number(!!a.isExist) - Number(!!b.isExist) || (a.city || '').localeCompare(b.city || ''));
                setRows(data);
            })
            .catch((err: Error) => {
                notify(`Failed to load City/State data: ${err?.message}`, { type: 'error', autoHideDuration: 5000 });
            })
            .finally(() => setLoading(false));
    }, [open, marketType]);

    /** Rows narrowed by the Exists / City / State / Invalid Only filters; feeds the grid directly. */
    const filteredRows = useMemo<CityStateRow[]>(() => rows.filter((row) => {
        if (existsFilter !== null && !!row.isExist !== existsFilter) return false;
        if (cityFilter && !(row.city || '').toLowerCase().includes(cityFilter.toLowerCase())) return false;
        if (stateFilter) {
            const haystack = `${row.stateId || ''} ${row.stateName || ''}`.toLowerCase();
            if (!haystack.includes(stateFilter.toLowerCase())) return false;
        }
        if (invalidOnlyFilter && !isRowInvalid(row)) return false;
        return true;
    }), [rows, existsFilter, cityFilter, stateFilter, invalidOnlyFilter]);

    /** Keeps `selectedCount` (drives the Insert/Merge button's visibility) in sync with the grid. */
    const handleSelectionChanged = (): void => {
        const api = agGridRef.current?.getAgGridInstance()?.api;
        setSelectedCount(api ? api.getSelectedRows().length : 0);
        if (!api || !editingMode) return;
        // Editing mode: remember every currently-visible row's checked state so it survives the
        // next filter change (rows filtered out of view are left untouched in the memory map).
        api.forEachNode((node: any) => {
            if (node.data) selectionMemoryRef.current[getRowKey(node.data)] = node.isSelected();
        });
    };

    /**
     * While the `No` filter is active (**Normal** mode only), auto-selects every currently
     * visible *valid* row — these are the City/State groups missing from `venue_city_states` and
     * the ones operators most often want to bulk-insert. Invalid rows (bad StateId/StateName/
     * Country) are deliberately left unchecked so operators must fix or explicitly select them
     * before merging. Re-runs whenever the filtered dataset changes (filter switch, City/State
     * search, or initial load) so selection always matches what's shown. Skipped entirely in
     * Editing mode, where selection is instead restored from {@link selectionMemoryRef}.
     */
    useEffect(() => {
        const api = agGridRef.current?.getAgGridInstance()?.api;
        if (!api || editingMode || existsFilter !== false) return;
        // Don't auto-select while the operator is actively applying a City Abbreviation change.
        if (isApplyingAbbr) return;
        // Auto-select every valid row in the filtered dataset; leave invalid rows unchecked.
        api.forEachNode((node: any) => {
            if (node.data) node.setSelected(!isRowInvalid(node.data as CityStateRow));
        });
    }, [filteredRows, existsFilter, editingMode, isApplyingAbbr]);

    /**
     * Editing mode only: restores each currently-visible row's checked state from
     * {@link selectionMemoryRef} whenever the filtered dataset changes (e.g. a filter is
     * narrowed, widened, or cleared). Rows not yet seen default to unchecked. This is what makes
     * previously checked rows reappear checked once filters are widened/cleared again.
     */
    useEffect(() => {
        if (!editingMode) return;
        const api = agGridRef.current?.getAgGridInstance()?.api;
        if (!api) return;
        api.forEachNode((node: any) => {
            if (!node.data) return;
            node.setSelected(!!selectionMemoryRef.current[getRowKey(node.data)]);
        });
        setSelectedCount(api.getSelectedRows().length);
    }, [filteredRows, editingMode]);

    /** Switching into Editing mode seeds the selection memory from whatever is currently checked, so nothing is lost. */
    const handleEditingModeChange = (value: boolean): void => {
        setEditingMode(value);
        if (!value) return;
        const api = agGridRef.current?.getAgGridInstance()?.api;
        if (!api) return;
        api.forEachNode((node: any) => {
            if (node.data) selectionMemoryRef.current[getRowKey(node.data)] = node.isSelected();
        });
    };

    /**
     * Reads the checked grid rows, blocks the operation if any of them are invalid, and — when
     * clear — opens the pre-merge confirmation dialog with the Insert/Update breakdown.
     * The actual `BulkMerge` call happens in {@link handleConfirmMerge}.
     */
    const handleRequestMerge = (): void => {
        const api = agGridRef.current?.getAgGridInstance()?.api;
        if (!api) return;

        api.stopEditing();
        const selectedRows = api.getSelectedRows() as CityStateRow[];

        if (selectedRows.length === 0) {
            notify('No rows selected to merge', { type: 'warning', autoHideDuration: 3000 });
            return;
        }

        if (selectedRows.some(isRowInvalid)) {
            // Mirrors the exact checks in isRowInvalid so operators know precisely which
            // condition(s) failed rather than a generic "invalid" warning.
            notify('One or more selected rows are invalid: State must be exactly 2 uppercase letters, State Name and Country must both be set, and City must be more than 2 characters long. Fix or deselect them before merging.', { type: 'warning', autoHideDuration: 6000 });
            return;
        }

        const toInsert = selectedRows.filter((row) => !(row.isExist || row._forceUpdateExisting)).length;
        setPendingMerge({ rows: selectedRows, toInsert, toUpdate: selectedRows.length - toInsert });
    };

    /**
     * Submits the rows confirmed in {@link handleRequestMerge} to the Bulk Merge endpoint.
     * A genuinely existing record (`isExist = true`, not redirected via `_forceUpdateExisting`)
     * is review-only aside from its CityAbbreviation: only `city`/`stateId` (to identify the
     * target record) and `cityAbbreviation` are submitted for it, so Description/Status/State
     * Name/Country — which belong to the existing venue_city_states record — are never
     * overwritten by stale grid data. Inserts and Check-Abbreviation-redirected updates still
     * submit the full field set.
     */
    const handleConfirmMerge = (): void => {
        if (!pendingMerge) return;
        const items = pendingMerge.rows.map((data) => {
            if (data.isExist && !data._forceUpdateExisting) {
                return {
                    city: data.city,
                    stateId: data.stateId,
                    cityAbbreviation: data.cityAbbreviation,
                    isExist: true,
                };
            }
            return {
                // A row redirected via Check City Abbreviation → "Update Existing" merges into the
                // matched venue_city_states row's City/StateId instead of its own.
                city: data._mergeTarget?.city || data.city,
                stateId: data._mergeTarget?.stateId || data.stateId,
                stateName: data.stateName,
                country: data.country,
                description: data.description,
                status: data.status,
                cityAbbreviation: data.cityAbbreviation,
                isExist: !!(data.isExist || data._forceUpdateExisting),
            };
        });

        setPendingMerge(null);
        setLoading(true);
        dataProvider
            .postData('CityStateCheck', 'BulkMerge', { items }, 'POST')
            .then((response: { data?: { success?: boolean; message?: string; inserted?: number; updated?: number } }) => {
                const { success, message, inserted = 0, updated = 0 } = response?.data || {};
                if (success) {
                    notify(`Merge complete: ${inserted} inserted, ${updated} updated.`, { type: 'success', autoHideDuration: 4000 });
                    if (onMerged) onMerged();
                    onClose();
                } else {
                    notify(message || 'Merge failed', { type: 'error', autoHideDuration: 4000 });
                }
            })
            .catch((err: Error) => {
                notify(`Bulk merge failed: ${err?.message}`, { type: 'error', autoHideDuration: 5000 });
            })
            .finally(() => setLoading(false));
    };

    /**
     * Sends the currently selected rows (MarketType, City, State, Country) to
     * `CityStateCheck/CheckCityAbbreviation`, which matches them against `venue_city_states`
     * using ignored-words-normalized City + exact State — catching near-duplicates (e.g.
     * "Radio City Music Hall" vs "Radio City") that the exact-string `isExist` check misses.
     * This only fetches and displays the results in {@link AbbrCheckResult}'s confirmation
     * dialog — it does not check/uncheck/update/remove any row; those actions only happen once
     * the operator explicitly confirms via **Update Existing City Abbreviation** or
     * **Apply Selected Changes**.
     */
    const handleCheckAbbreviation = (): void => {
        const api = agGridRef.current?.getAgGridInstance()?.api;
        if (!api) return;

        api.stopEditing();
        const selectedRows = api.getSelectedRows() as CityStateRow[];
        if (selectedRows.length === 0) {
            notify('No rows selected to check', { type: 'warning', autoHideDuration: 3000 });
            return;
        }

        setCheckingAbbr(true);
        dataProvider
            .postData('CityStateCheck', 'CheckCityAbbreviation', { items: selectedRows.filter(x => !x._forceUpdateExisting) }, 'POST')
            .then((response: { data?: { success?: boolean; message?: string; matches?: CityAbbreviationMatch[]; selfMatches?: CityAbbreviationSelfMatch[] } }) => {
                const { success, message, matches = [], selfMatches = [] } = response?.data || {};
                if (!success) {
                    notify(message || 'Check City Abbreviation failed', { type: 'error', autoHideDuration: 4000 });
                    return;
                }
                setAbbrCheckResult({ matches, selfMatches, selectedRows });
            })
            .catch((err: Error) => {
                notify(`Check City Abbreviation failed: ${err?.message}`, { type: 'error', autoHideDuration: 5000 });
            })
            .finally(() => setCheckingAbbr(false));
    };

    /** Applies the selected bulk status to every currently loaded row (independent of the active filters). */
    const handleApplyStatus = (): void => {
        const status = Number(applyStatusValue);
        setRows((prev) => prev.map((r) => ({ ...r, status })));
        notify(`Status ${status} applied to ${rows.length} row(s).`, { type: 'info', autoHideDuration: 4000 });
    };

    /**
     * Stages (Step 1 of the Check Abbreviation dialog) the operator's Keep/Uncheck/None choice
     * (see {@link selfMatchChoice}) for every self-match pair: pairs left at **None** are skipped
     * entirely; the rest have their resolved "drop" row queued for deselection and the (editable)
     * merged CityAbbreviation prompted for the resolved "keep" row applied to
     * {@link pendingAbbrRows}. The dialog stays open and the step is marked complete (disabling its
     * controls) — nothing is written back to `rows`/the grid until the operator clicks **OK**
     * ({@link handleCommitAbbrCheck}).
     */
    const handleApplySelectedChanges = (): void => {
        if (!abbrCheckResult?.selfMatches?.length || step1Completed) return;

        const keepIdByPair: Record<string, string> = {};
        const dropIdsSet = new Set<string>(); // Keep track of drop IDs to deselect once committed.
        let appliedCount = 0;
        abbrCheckResult.selfMatches.forEach((sm) => {
            const pairKey = `${sm.keepItemId}|${sm.dropItemId}`;
            const choice = selfMatchChoice[pairKey] || 'none';
            if (choice === 'none') return; // Leave this pair entirely unchanged.
            appliedCount += 1;
            const swap = choice === 'drop';
            const keepId = swap ? sm.dropItemId : sm.keepItemId;
            const dropId = swap ? sm.keepItemId : sm.dropItemId;
            keepIdByPair[pairKey] = keepId;
            dropIdsSet.add(dropId);
        });

        if (appliedCount === 0) {
            notify('No changes selected — every pair was left at None.', { type: 'info', autoHideDuration: 4000 });
            setStep1Completed(true);
            return;
        }

        // Stage the updated city abbreviations on top of whatever Step 2 may have already staged.
        const base = pendingAbbrRows ?? rows;
        const updatedRows = base.map((r) => {
            const pairKey = Object.keys(keepIdByPair).find((key) => keepIdByPair[key] === r.id);
            if (!pairKey) return r;
            const cityAbbreviation = selfMatchAbbreviations[pairKey];
            if (cityAbbreviation === undefined) return r;
            return { ...r, cityAbbreviation };
        });
        setPendingAbbrRows(updatedRows);
        setPendingDeselectIds((prev) => new Set([...prev, ...dropIdsSet]));
        setStep1Completed(true);
        notify(`Staged changes for ${appliedCount} duplicate row pair(s) — click OK to save.`, { type: 'info', autoHideDuration: 5000 });
    };

    /**
     * Updates the Keep/Uncheck/None choice for a self-match pair and recomputes the suggested
     * merged CityAbbreviation preview for whichever row is now the "keep" side. Purely local UI
     * state — no row or grid selection is mutated until {@link handleApplySelectedChanges} runs.
     */
    const handleSelfMatchChoiceChange = (sm: CityAbbreviationSelfMatch, choice: SelfMatchChoice): void => {
        const pairKey = `${sm.keepItemId}|${sm.dropItemId}`;
        setSelfMatchChoice((prev) => ({ ...prev, [pairKey]: choice }));
        if (choice === 'none') return;

        const keepId = choice === 'drop' ? sm.dropItemId : sm.keepItemId;
        const dropId = choice === 'drop' ? sm.keepItemId : sm.dropItemId;
        const keepRow = abbrCheckResult?.selectedRows.find((r) => r.id === keepId);
        const dropRow = abbrCheckResult?.selectedRows.find((r) => r.id === dropId);
        setSelfMatchAbbreviations((prev) => ({
            ...prev,
            [pairKey]: mergeCityAbbreviation(keepRow?.cityAbbreviation, dropRow?.city, dropRow?.cityAbbreviation),
        }));
    };

    /**
     * Stages (Step 2 of the Check Abbreviation dialog) a redirect for every row for which the
     * operator explicitly picked a match (radio choice other than the default **None**), so it
     * will update that matched `venue_city_states` row's CityAbbreviation instead of inserting a
     * new record. Rows left at **None** are untouched. The dialog stays open and the step is
     * marked complete (disabling its controls) — nothing is written back to `rows`/the grid until
     * the operator clicks **OK** ({@link handleCommitAbbrCheck}).
     */
    const handleUpdateExisting = (): void => {
        const entries = Object.entries(matchSelections);
        if (!abbrCheckResult || entries.length === 0 || step2Completed) {
            notify('Select a match for at least one row before updating.', { type: 'warning', autoHideDuration: 4000 });
            return;
        }

        const base = pendingAbbrRows ?? rows;
        const updatedRows = base.map((r) => {
            const match = matchSelections[r.id];
            if (!match) return r;
            return {
                ...r,
                _forceUpdateExisting: true,
                _mergeTarget: { city: match.city || r.city || '', stateId: match.state || r.stateId || '' },
                city: match.city || r.city || '',
                stateId: match.state || r.stateId || '',
                // match.state carries only the DB state *abbreviation* (e.g. "MA"), not a full state
                // name, so prefer the row's existing stateName to avoid showing the abbreviation here.
                stateName: r.stateName || match.state || '',
                country: match.country || r.country || '',
                cityAbbreviation: getCityAbbreviation(r, match),
            };
        });
        setPendingAbbrRows(updatedRows);
        setStep2Completed(true);
        notify(`${entries.length} row(s) staged to update their matched venue_city_states record instead of inserting a new one — click OK to save.`, { type: 'info', autoHideDuration: 5000 });
    };

    /**
     * **OK** — commits every staged change from the current Check Abbreviation dialog session
     * ({@link pendingAbbrRows}) into `rows`/the grid (syncing AgGrid's row data and deselecting any
     * rows Step 1 resolved as "drop"), then closes the dialog. A no-op on `rows` if neither step
     * was applied (nothing staged).
     */
    const handleCommitAbbrCheck = (): void => {
        if (pendingAbbrRows) {
            // Mark that we're applying changes so the auto-select effect doesn't interfere with the grid selection.
            setIsApplyingAbbr(true);
            setRows(pendingAbbrRows);
            const api = agGridRef.current?.getAgGridInstance()?.api;
            if (api) {
                // Update the entire data store of the Grid to immediately synchronize the modified text cells.
                api.setRowData(pendingAbbrRows);
                // Deselect the rows Step 1 resolved as "drop".
                api.forEachNode((node: any) => {
                    if (node.data && (pendingDeselectIds.has(node.data.id) || pendingDeselectIds.has(node.data.itemId))) {
                        node.setSelected(false);
                    }
                });
            }
            handleSelectionChanged();
            notify('Check Abbreviation changes saved.', { type: 'success', autoHideDuration: 4000 });
        }
        setAbbrCheckResult(null);
    };

    /**
     * **Cancel** — discards every staged change from the current Check Abbreviation dialog
     * session without touching `rows`/the grid, then closes the dialog.
     */
    const handleCancelAbbrCheck = (): void => {
        if (pendingAbbrRows) {
            notify('Check Abbreviation changes discarded.', { type: 'info', autoHideDuration: 4000 });
        }
        setAbbrCheckResult(null);
    };

    return (
        <>
            <MDialog
                open={open}
                title={`City/State Check${marketType ? ` — ${marketType}` : ''} (${filteredRows.length} of ${rows.length} group(s))`}
                handleClose={onClose}
                maxWidth="xl"
                disabled={loading || checkingAbbr}
                msgLoading={loading ? 'Loading...' : (checkingAbbr ? 'Checking City Abbreviation...' : undefined)}
                action={
                    selectedCount > 0 && (
                        <>
                            <Button
                                type="button"
                                variant="outlined"
                                color="primary"
                                startIcon={<FactCheckIcon />}
                                onClick={handleCheckAbbreviation}
                                disabled={loading || checkingAbbr}
                                sx={{ fontWeight: 600, textTransform: 'none' }}
                            >
                                Check City Abbreviation
                            </Button>
                            <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                startIcon={<PlaylistAddCheckIcon />}
                                onClick={handleRequestMerge}
                                disabled={loading || checkingAbbr}
                                sx={{ fontWeight: 600, textTransform: 'none', boxShadow: 2 }}
                            >
                                Insert / Merge Selected ({selectedCount})
                            </Button>
                        </>
                    )
                }
            >
                {rows.length === 0 && !loading && (
                    <Typography color="text.secondary">No orphaned City/State groups found.</Typography>
                )}

                {rows.length > 0 && (
                    <FilterBar
                        onExistsFilterChange={setExistsFilter}
                        cityFilter={cityFilter}
                        onCityFilterChange={setCityFilter}
                        stateFilter={stateFilter}
                        onStateFilterChange={setStateFilter}
                        invalidOnlyFilter={invalidOnlyFilter}
                        onInvalidOnlyFilterChange={setInvalidOnlyFilter}
                        editingMode={editingMode}
                        onEditingModeChange={handleEditingModeChange}
                        applyStatusValue={applyStatusValue}
                        onApplyStatusValueChange={setApplyStatusValue}
                        onApplyStatus={handleApplyStatus}
                    />
                )}

                {rows.length > 0 && filteredRows.length === 0 && !loading && (
                    <Typography color="text.secondary" sx={{ mb: 1 }}>No City/State groups match the current filters.</Typography>
                )}

                <CityStateGrid ref={agGridRef} rows={filteredRows} onSelectionChanged={handleSelectionChanged} />
            </MDialog>

            <MergeDialogs
                pendingMerge={pendingMerge}
                onCancelPendingMerge={() => setPendingMerge(null)}
                onConfirmMerge={handleConfirmMerge}
            />

            <CheckAbbreviationDialog
                abbrCheckResult={abbrCheckResult}
                matchSelections={matchSelections}
                onMatchSelectionChange={(rowId, idx, matchList) => setMatchSelections((prev) => {
                    if (idx === 'none') {
                        const { [rowId]: _omit, ...rest } = prev;
                        return rest;
                    }
                    return { ...prev, [rowId]: matchList[Number(idx)] };
                })}
                onUpdateExisting={handleUpdateExisting}
                selfMatchChoice={selfMatchChoice}
                onSelfMatchChoiceChange={handleSelfMatchChoiceChange}
                selfMatchAbbreviations={selfMatchAbbreviations}
                onSelfMatchAbbreviationChange={(pairKey, value) => setSelfMatchAbbreviations((prev) => ({ ...prev, [pairKey]: value }))}
                onApplySelectedChanges={handleApplySelectedChanges}
                step1Completed={step1Completed}
                step2Completed={step2Completed}
                onCommit={handleCommitAbbrCheck}
                onCancel={handleCancelAbbrCheck}
            />
        </>
    );
};

export default CityStateDetailPopup;
